import React, { createContext, useEffect, useMemo, useState } from "react";
import { PortalSettingsState } from ".";
import { PortalSettingsContextProviderProps, PortalSettingsStateSetters } from "./types";

export const PortalSettingsStateContext = createContext<PortalSettingsState>(undefined);
PortalSettingsStateContext.displayName = "PortalSettingsStateContext";

export const PortalSettingsStateSetterContext = createContext<PortalSettingsStateSetters>(undefined);
PortalSettingsStateSetterContext.displayName = "PortalSettingsStateSetterContext";

/**
 * A context provider that provides a global "portal settings" state for its children sub-tree. These setting can be
 * used by descendent components that use React Portal. Currently there is only one setting, named `disabled`, that
 * allows to completely disable the use of Portals at a global level.
 *
 * Within the Blueprint library it is used by the `Overlay` component. It either render the overlay content using a
 * Portal or not depending on the `disabled` setting within this context. Note that this also affects the behavior of
 * all other Blueprint components that directly or indirectly use the `Overlay` component - e.g. `Popover`.
 *
 * It has 2 companion hooks to use its functionality:
 *
 * ### `useIsPortalDisabled`
 *
 * This hook returns the current `disabled` state maintained by this provider.
 *
 * ### `useSetIsPortalDisabled`
 *
 * This hook returns a state setter function that can be used to update the `disabled` state in the context provider.
 * When this happens, all component within this provider that use this state via the `useIsPortalDisabled` hook will be
 * re-rendered using the updated state.
 */
export const PortalSettingsContextProvider = ({
  disabled: disabledProp,
  children,
}: PortalSettingsContextProviderProps) => {
  const [disabled, setDisabled] = useState(disabledProp);

  useEffect(() => {
    setDisabled((disabledState) => (disabledProp !== disabledState ? disabledProp : disabledState));
  }, [disabledProp]);

  const stateSetters = useMemo(() => ({ setDisabled }), []);

  return (
    <PortalSettingsStateContext.Provider value={{ disabled }}>
      <PortalSettingsStateSetterContext.Provider value={stateSetters}>
        {children}
      </PortalSettingsStateSetterContext.Provider>
    </PortalSettingsStateContext.Provider>
  );
};
